<script setup lang="ts">
import { ref } from 'vue'
import axios from 'axios'
import {
  X, Calendar, Download, Link2, Copy, Check, RefreshCw, Loader2,
} from 'lucide-vue-next'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits(['close'])

const feedUrl = ref('')
const isLoading = ref(false)
const isCopied = ref(false)
const isRegenerating = ref(false)
const error = ref('')

async function loadFeedUrl() {
  if (feedUrl.value) return

  isLoading.value = true
  error.value = ''
  try {
    const response = await axios.get('/calendar/subscribe-url')
    feedUrl.value = response.data.url
  } catch (err) {
    error.value = 'Failed to load calendar feed URL'
  } finally {
    isLoading.value = false
  }
}

async function regenerateUrl() {
  isRegenerating.value = true
  error.value = ''
  try {
    const response = await axios.post('/calendar/regenerate-token')
    feedUrl.value = response.data.url
  } catch (err) {
    error.value = 'Failed to regenerate URL'
  } finally {
    isRegenerating.value = false
  }
}

async function copyUrl() {
  try {
    await navigator.clipboard.writeText(feedUrl.value)
    isCopied.value = true
    setTimeout(() => { isCopied.value = false }, 2000)
  } catch {
    // Fallback for older browsers
    const input = document.createElement('input')
    input.value = feedUrl.value
    document.body.appendChild(input)
    input.select()
    document.execCommand('copy')
    document.body.removeChild(input)
    isCopied.value = true
    setTimeout(() => { isCopied.value = false }, 2000)
  }
}

function downloadIcs() {
  window.location.href = '/calendar/download'
}

// Load feed URL when modal opens
import { watch } from 'vue'
watch(() => props.show, (val) => {
  if (val) loadFeedUrl()
})
</script>

<template>
  <Transition name="modal">
    <div
      v-if="show"
      class="fixed inset-0 z-50 overflow-y-auto"
      @click.self="emit('close')"
    >
      <!-- Backdrop -->
      <div class="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity"></div>

      <!-- Modal Container -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl" @click.stop>
          <!-- Header -->
          <div class="bg-linear-to-r from-purple-600 to-blue-600 rounded-t-2xl px-6 py-4 flex items-center justify-between">
            <div class="flex items-center gap-3">
              <Calendar class="w-5 h-5 text-white" />
              <h3 class="text-lg font-bold text-white">Sync to Calendar</h3>
            </div>
            <button @click="emit('close')" class="text-white/80 hover:text-white transition-colors">
              <X class="w-5 h-5" />
            </button>
          </div>

          <!-- Content -->
          <div class="p-6 space-y-6">
            <!-- Option 1: Subscribe URL -->
            <div>
              <h4 class="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                <Link2 class="w-4 h-4 text-purple-600" />
                Subscribe (Auto-updates)
              </h4>
              <p class="text-sm text-gray-500 mb-3">
                Add this URL to Google Calendar, Apple Calendar, or Outlook. Your schedule will update automatically.
              </p>

              <div v-if="isLoading" class="flex items-center gap-2 text-gray-400 py-3">
                <Loader2 class="w-4 h-4 animate-spin" />
                <span class="text-sm">Loading feed URL...</span>
              </div>

              <div v-else-if="error" class="text-sm text-red-500 py-2">{{ error }}</div>

              <div v-else class="space-y-3">
                <div class="flex items-center gap-2">
                  <input
                    :value="feedUrl"
                    readonly
                    class="flex-1 text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-700 select-all"
                    @focus="($event.target as HTMLInputElement).select()"
                  />
                  <button
                    @click="copyUrl"
                    :class="[
                      'flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      isCopied
                        ? 'bg-green-100 text-green-700'
                        : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                    ]"
                  >
                    <component :is="isCopied ? Check : Copy" class="w-4 h-4" />
                    {{ isCopied ? 'Copied' : 'Copy' }}
                  </button>
                </div>

                <button
                  @click="regenerateUrl"
                  :disabled="isRegenerating"
                  class="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <RefreshCw :class="['w-3 h-3', isRegenerating ? 'animate-spin' : '']" />
                  {{ isRegenerating ? 'Regenerating...' : 'Regenerate URL (invalidates old one)' }}
                </button>
              </div>

              <!-- Quick setup instructions -->
              <div class="mt-4 bg-gray-50 rounded-lg p-3 space-y-2">
                <p class="text-xs font-semibold text-gray-600">Quick Setup:</p>
                <ul class="text-xs text-gray-500 space-y-1">
                  <li><span class="font-medium">Google Calendar:</span> Settings &rarr; Add calendar &rarr; From URL &rarr; Paste URL</li>
                  <li><span class="font-medium">Apple Calendar:</span> File &rarr; New Calendar Subscription &rarr; Paste URL</li>
                  <li><span class="font-medium">Outlook:</span> Add calendar &rarr; Subscribe from web &rarr; Paste URL</li>
                </ul>
              </div>
            </div>

            <!-- Divider -->
            <div class="flex items-center gap-3">
              <div class="flex-1 border-t border-gray-200"></div>
              <span class="text-xs text-gray-400 font-medium">OR</span>
              <div class="flex-1 border-t border-gray-200"></div>
            </div>

            <!-- Option 2: Download -->
            <div>
              <h4 class="font-semibold text-gray-900 flex items-center gap-2 mb-2">
                <Download class="w-4 h-4 text-blue-600" />
                Download (.ics)
              </h4>
              <p class="text-sm text-gray-500 mb-3">
                Download a one-time snapshot of your schedule. Open the file to import into any calendar app.
              </p>
              <button
                @click="downloadIcs"
                class="flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Download class="w-4 h-4" />
                Download Schedule
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>
